// WCC Projects > Change of Routine
// src > ts > model.ts
// Created 1 March 2021, Updated 14 October 2024 (Version 3.0.5)

// TYPES

export enum ProcessStageState {
  None = "NONE",
  Approved = "APPROVED",
  Rejected = "REJECTED",
  Cancelled = "CANCELLED",
}

export enum FormType {
  ProfessionalLearningApplication = "Professional Learning Application",
  MeetingApplication = "Meeting Application",
  LeaveApplication = "Leave Application",
  ExcursionOrActivityApplication = "Excursion/In School Activity Application",
  ExcursionApplication = "Excursion Application",
  IncursionApplication = "Incursion Application",
}

export interface ProcessStage {
  email: string;
  state: ProcessStageState;
  comment: string | null;
  date: Date | null;
}

export interface Form extends DatabaseFormContent {
  id: string;
}

export interface DatabaseFormContent {
  viewers: string[];
  editors: string[];
  type: FormType;
  editDate?: (Date | null);
  submitDate: (Date | null);
  process: ProcessStage[];
  data: any;
  attachments: string[];
  isTestForm?: boolean;
  isSuspended?: boolean;
}

export interface Organisation {
  name: string;
  usersData: UserData[];
}

export interface UserGroup {
  // id: string;
  title: string;
  members: string[];
}

export interface UserData {
  email: string,
  firstName?: string, // DEPRECATED
  lastName?: string, // DEPRECATED
  fullName: string,
  role?: string,
}

export interface FormTemplate {
  // id: string;
  title: string;
  createGroup: string;
  archiveGroup: string;
  process: ProcessTemplate[];
}

export interface ProcessTemplate {
  type: ProcessTemplateType;
  approvers: string[];
}

export enum ProcessTemplateType {
  group1 = '1 Group',
  specified1 = '1 Specified',
  specified2 = '2 Specified',
}

export interface ProcessTemplateGroup1 extends ProcessTemplate {
  type: ProcessTemplateType.group1;
}

export interface ProcessTemplateSpecified1 extends ProcessTemplate {
  type: ProcessTemplateType.specified1;
}

export interface ProcessTemplateSpecified2 extends ProcessTemplate {
  type: ProcessTemplateType.specified2;
}

export interface ProfessionalLearningApplicationFormData {
  titleOfInservice: string;
  name: string;
  department: string;
  dateOfInservice: string;
  dateOfInserviceTo: string;
  timeFrom: string;
  timeTo: string;
  paidRelief: (boolean | null);
  paidReliefCodeNumber: string;
  cost: string;
  venue: string;
  venueBooked: (string | null);
  peopleOnsite?: (string | null);
  contactPhoneNumber: string;
  educationalAims: string;
  informationSharedWholeStaff: (boolean | null);
  informationSharedSupportStaff: (boolean | null);
  informationSharedOther: (boolean | null);
  throughStaffMeeting: (boolean | null);
  throughLeadershipTeam: (boolean | null);
  throughOther: (boolean | null);
  supportingDocumentation?: (string | null);
}

export interface MeetingApplicationFormData {
  titleOfMeeting: string;
  name: string;
  externalVisitors?: (boolean | null);
  dateOfMeeting: string;
  dateOfMeetingTo: string;
  timeFrom: string;
  timeTo: string;
  paidRelief: (boolean | null);
  paidReliefCodeNumber: string;
  cost: string;
  venue: string;
  venueBooked: (string | null);
  peopleOnsite?: (string | null);
  contactPhoneNumber: string;
  educationalAims: string;
  informationSharedWholeStaff: (boolean | null);
  informationSharedSupportStaff: (boolean | null);
  informationSharedOther: (boolean | null);
  throughStaffMeeting: (boolean | null);
  throughLeadershipTeam: (boolean | null);
  throughOther: (boolean | null);
  supportingDocumentation?: (string | null);
}

export interface LeaveApplicationFormData {
  formTitle: string;
  name: string;
  department: (string | null);
  dateOfLeave: string;
  dateOfLeaveTo: string;
  dayDuration: (string | null);
  timeFrom: string;
  timeTo: string;
  typeOfLeave: (string | null);
  otherTypeOfLeave: string;
  confirmationCedpForm: (boolean | null);
  confirmationCedpApplication: (boolean | null);
  confirmation2HoursLess: (boolean | null);
  confirmation2HoursMore: (boolean | null);
  confirmation7DaysNotice: (boolean | null);
  supportingDocumentation: string;
}

export interface ExcursionInSchoolActivityApplicationFormData {
  formTitle: string;
  activityType: (string | null);
  department: string;
  venueOfExcursion: string;
  venueBooked: (boolean | null);
  address: string;
  date: string;
  dateTo?: (string | null);
  organisingTeachersName?: string;
  organisingTeachersDetails?: string;
  code1?: (string | null);
  name1?: (string | null);
  code2?: (string | null);
  name2?: (string | null);
  code3?: (string | null);
  name3?: (string | null);
  code4?: (string | null);
  name4?: (string | null);
  code5?: (string | null);
  name5?: (string | null);
  code6?: (string | null);
  name6?: (string | null);
  code7?: (string | null);
  name7?: (string | null);
  code8?: (string | null);
  name8?: (string | null);
  classesInvolved?: string;
  numberOfStudents: string;
  teacherInCharge: string;
  contactNumber?: string;
  areOtherStaffAttending: (boolean | null);
  otherStaffAttending: string;
  timeOfDeparture: string;
  timeOfReturn: string;
  educationalObjectives: string;
  staffWithFirstAid: string;
  includesWaterSports: (boolean | null);
  includesWaterSportsCprStaff: string;
  bcscCompleted: (boolean | null);
  proposedCost: string;
  schoolFeeBilling: (boolean | null);
  costIncludedInBudget: (boolean | null);
  externalProviderEngaged?: (string | null);
  providerContactNumber?: string;
  providerRiskAssessment?: (string | null);
  transportArrangement: string;
  studentDressCode: (string | null);
  studentPermission: (string | null);
  schoolCalendarChecked: (boolean | null);
  assessmentCalendarChecked: (boolean | null);
  riskAssessment: (boolean | null);
  transportOrganised?: (boolean | null);
  visitorsCompletedProcedure: (string | null);
  bookFirstAidKit: (string | null);
  listOfStudentsToServices: (string | null);
  canteenAdvised: (string | null);
  supportingDocumentation?: (string | null);
}

export interface ExcursionApplicationFormData {
  formTitle: string;
  department: string;
  venue: string;
  venueBooked: (boolean | null);
  address: string;
  date: string;
  dateTo?: (string | null);
  startTime: string;
  endTime: string;
  organisingTeachersName?: string;
  organisingTeachersDetails?: string;
  code1?: (string | null);
  name1?: (string | null);
  code2?: (string | null);
  name2?: (string | null);
  code3?: (string | null);
  name3?: (string | null);
  code4?: (string | null);
  name4?: (string | null);
  code5?: (string | null);
  name5?: (string | null);
  code6?: (string | null);
  name6?: (string | null);
  code7?: (string | null);
  name7?: (string | null);
  code8?: (string | null);
  name8?: (string | null);
  numberOfStudents: string;
  teacherInCharge: string;
  areOtherStaffAttending: (boolean | null);
  otherStaffAttending: string;
  timeOfDeparture: string;
  timeOfReturn: string;
  educationalObjectives: string;
  staffWithFirstAid: string;
  includesWaterSports: (boolean | null);
  includesWaterSportsCprStaff: string;
  proposedCost: string;
  schoolFeeBilling: (boolean | null);
  schoolFinance: (boolean | null);
  costIncludedInBudget: (boolean | null);
  externalProviderEngaged?: (string | null);
  providerContactNumber?: string;
  providerRiskAssessment?: (string | null);
  transportWalk: (boolean | null);
  transportSchoolBus: (boolean | null);
  transportPrivateBus: (boolean | null);
  transportPublic: (boolean | null);
  transportParent: (boolean | null);
  studentDressCode: (string | null);
  studentPermission: (string | null);
  schoolCalendarChecked: (boolean | null);
  assessmentCalendarChecked: (boolean | null);
  riskAssessment: (boolean | null);
  visitorsCompletedProcedure: (string | null);
  bookFirstAidKit: (string | null);
  listOfStudentsToServices: (string | null);
  canteenAdvised: (string | null);
  freshdeskTicketLogged: (string | null);
  supportingDocumentation?: (string | null);
}

export interface IncursionApplicationFormData {
  formTitle: string;
  department: string;
  venue: string;
  venueBooked: (boolean | null);
  date: string;
  dateTo?: (string | null);
  startTime: string;
  endTime: string;
  organisingTeachersName?: string;
  organisingTeachersDetails?: string;
  code1?: (string | null);
  name1?: (string | null);
  code2?: (string | null);
  name2?: (string | null);
  code3?: (string | null);
  name3?: (string | null);
  code4?: (string | null);
  name4?: (string | null);
  code5?: (string | null);
  name5?: (string | null);
  code6?: (string | null);
  name6?: (string | null);
  code7?: (string | null);
  name7?: (string | null);
  code8?: (string | null);
  name8?: (string | null);
  numberOfStudents: string;
  teacherInCharge: string;
  areOtherStaffAttending: (boolean | null);
  otherStaffAttending: string;
  educationalObjectives: string;
  proposedCost: string;
  schoolFeeBilling: (boolean | null);
  schoolFinance: (boolean | null);
  costIncludedInBudget: (boolean | null);
  externalProviderEngaged?: (string | null);
  providerContactNumber?: string;
  providerRiskAssessment?: (string | null);
  studentDressCode: (string | null);
  studentPermission: (string | null);
  schoolCalendarChecked: (boolean | null);
  assessmentCalendarChecked: (boolean | null);
  riskAssessment: (boolean | null);
  visitorsCompletedProcedure: (string | null);
  bookFirstAidKit: (string | null);
  listOfStudentsToServices: (string | null);
  canteenAdvised: (string | null);
  freshdeskTicketLogged: (string | null);
  supportingDocumentation?: (string | null);
}
